import {
  ChevronFirst,
  ChevronLast,
  ChevronLeftIcon,
  ChevronRightIcon,
} from 'lucide-react';
import { Button } from '../Button';
import { Tooltip } from '../Tooltip';
import useI18n from '../../hooks/useI18n';
import { getPageRange } from './utils';

export type PaginatorProps = {
  /** Current page, 1-based. */
  page: number;
  /** Total number of pages. Renders nothing when 1 or less. */
  pageCount: number;
  /** Called with the new 1-based page number. */
  onPageChange: (page: number) => void;
  /** Pages shown on each side of the current page. */
  siblingCount?: number;
  className?: string;
  /** Accessible label for the nav landmark. Use a unique value per page when multiple paginators are present. */
  'aria-label'?: string;
  /** The id of the table element this pagination controls. */
  'aria-controls'?: string;
};

export function Paginator({
  page,
  pageCount,
  onPageChange,
  siblingCount = 3,
  className,
  'aria-label': ariaLabel,
  'aria-controls': ariaControls,
}: PaginatorProps) {
  const { t } = useI18n();

  if (pageCount <= 1) return <div />;

  const windowSize = siblingCount + 1 + siblingCount;
  const showEdgeButtons = pageCount > windowSize + 1;

  return (
    <nav aria-label={ariaLabel ?? t('pagination')} aria-controls={ariaControls} className={className}>
      <ul
        className='eye:m-0 eye:flex eye:list-none eye:items-center eye:justify-center eye:gap-x-4xs eye:p-0'
      >
        {showEdgeButtons && (
          <li>
            <Tooltip content={t('firstPage')}>
              <Button
                variant='ghost'
                size='icon'
                aria-label={t('firstPage')}
                onClick={() => onPageChange(1)}
                disabled={page <= 1}
              >
                <ChevronFirst aria-hidden />
              </Button>
            </Tooltip>
          </li>
        )}
        <li>
          <Tooltip content={t('previousPage')}>
            <Button
              variant='ghost'
              size='icon'
              aria-label={t('previousPage')}
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeftIcon aria-hidden />
            </Button>
          </Tooltip>
        </li>
        {getPageRange(page, pageCount, siblingCount).map((pageNumber) => (
          <li key={pageNumber}>
            <Button
              variant={pageNumber === page ? 'black' : 'ghost'}
              size='icon'
              aria-label={t(pageNumber === page ? 'currentPage' : 'goToPage', {
                page: pageNumber,
              })}
              aria-current={pageNumber === page ? 'page' : undefined}
              onClick={() => onPageChange(pageNumber)}
            >
              {pageNumber}
            </Button>
          </li>
        ))}
        <li>
          <Tooltip content={t('nextPage')}>
            <Button
              variant='ghost'
              size='icon'
              aria-label={t('nextPage')}
              onClick={() => onPageChange(page + 1)}
              disabled={page >= pageCount}
            >
              <ChevronRightIcon aria-hidden />
            </Button>
          </Tooltip>
        </li>
        {showEdgeButtons && (
          <li>
            <Tooltip content={t('lastPage')}>
              <Button
                variant='ghost'
                size='icon'
                aria-label={t('lastPage')}
                onClick={() => onPageChange(pageCount)}
                disabled={page >= pageCount}
              >
                <ChevronLast aria-hidden />
              </Button>
            </Tooltip>
          </li>
        )}
      </ul>
    </nav>
  );
}
